import React, { useEffect, useState } from "react";
import { Radio, Square, Loader2, History, Heart, Eye, Trash2, Satellite, CheckCircle2, ShieldCheck, HelpCircle } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { PLATFORMS } from "@/components/community/StreamEmbed";
import ConnectAccounts from "@/components/community/ConnectAccounts";

const inp = "w-full px-3 py-2.5 rounded-lg bg-black/30 border border-white/10 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[hsl(var(--primary))]";

export default function GoLivePanel() {
  const [data, setData] = useState(null);
  const [f, setF] = useState({ platform: "youtube", channel_url: "", title: "", game: "", tags: "" });
  const [busy, setBusy] = useState(false);
  const [chs, setChs] = useState(null);
  const [detect, setDetect] = useState({});
  const [cf, setCf] = useState({ platform: "youtube", channel_url: "", game: "" });
  const [cbusy, setCbusy] = useState(false);

  const loadCh = () => api.get("/streams/channels/mine").then((r) => { setChs(r.data.items); setDetect(r.data.detect_available); }).catch(() => setChs([]));
  useEffect(() => { loadCh(); }, []);

  const addChannel = async () => {
    if (!cf.channel_url.trim()) return toast.error("Paste your channel link");
    setCbusy(true);
    try { await api.post("/streams/channels", cf); toast.success("Channel registered — auto-detect is on"); setCf({ ...cf, channel_url: "" }); loadCh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not register"); }
    finally { setCbusy(false); }
  };
  const removeChannel = async (p) => { try { await api.delete(`/streams/channels/${p}`); loadCh(); } catch { toast.error("Failed"); } };

  const load = () => api.get("/streams/mine").then((r) => {
    setData(r.data);
    if (r.data.stream) setF({ platform: r.data.stream.platform, channel_url: r.data.stream.channel_url, title: r.data.stream.title, game: r.data.stream.game, tags: (r.data.stream.tags || []).join(", ") });
  }).catch(() => setData({ stream: null, history: [] }));
  useEffect(() => { load(); }, []);

  const golive = async () => {
    if (!f.channel_url.trim()) return toast.error("Paste your stream link");
    setBusy(true);
    try {
      await api.post("/streams/golive", { ...f, tags: f.tags.split(",").map((t) => t.trim()).filter(Boolean) });
      toast.success("You are live! Followers notified.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not go live"); }
    finally { setBusy(false); }
  };
  const offline = async () => {
    setBusy(true);
    try { await api.post("/streams/offline"); toast.success("Stream ended — saved to your history"); load(); }
    catch { toast.error("Failed"); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="card-surface rounded-2xl p-6 mb-8 text-white/40"><Loader2 className="animate-spin" /></div>;
  const live = data.stream?.is_live;

  return (
    <div className="card-surface rounded-2xl p-5 md:p-6 mb-8" data-testid="golive-panel">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-white flex items-center gap-2"><Radio size={18} className="text-[hsl(var(--primary))]" /> My Stream</h2>
        {live && <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-600 text-white text-xs font-bold live-dot" data-testid="golive-live-badge">● LIVE · {data.stream.viewers} watching</span>}
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-white/50 mb-1 block">Platform</label>
          <div className="flex flex-wrap gap-2" data-testid="golive-platforms">
            {Object.entries(PLATFORMS).map(([k, p]) => (
              <button key={k} onClick={() => setF({ ...f, platform: k })} data-testid={`golive-platform-${k}`}
                className={`px-3 py-2 rounded-lg text-xs font-bold border transition ${f.platform === k ? "border-[hsl(var(--primary))] text-white" : "border-white/10 text-white/50"}`}
                style={f.platform === k ? { background: p.color + "22" } : {}}>{p.label}</button>
            ))}
          </div>
        </div>
        <div><label className="text-xs text-white/50 mb-1 block">Game</label><input className={inp} value={f.game} onChange={(e) => setF({ ...f, game: e.target.value })} placeholder="e.g. VALORANT" data-testid="golive-game" /></div>
      </div>

      <div className="mt-3"><label className="text-xs text-white/50 mb-1 block">Your stream link ({PLATFORMS[f.platform].label})</label>
        <input className={inp} value={f.channel_url} onChange={(e) => setF({ ...f, channel_url: e.target.value })} placeholder={f.platform === "youtube" ? "https://youtube.com/watch?v=… (live video)" : f.platform === "nimotv" ? "https://www.nimo.tv/embed/12345678 (Room ID)" : `https://${f.platform}.com/yourchannel`} data-testid="golive-url" /></div>

      <div className="mt-3 grid sm:grid-cols-2 gap-3">
        <div><label className="text-xs text-white/50 mb-1 block">Title</label><input className={inp} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Ranked grind to Radiant" data-testid="golive-title" /></div>
        <div><label className="text-xs text-white/50 mb-1 block">Tags (comma separated)</label><input className={inp} value={f.tags} onChange={(e) => setF({ ...f, tags: e.target.value })} placeholder="English, Pro Player" data-testid="golive-tags" /></div>
      </div>

      <div className="mt-5 flex gap-3">
        {!live ? (
          <button onClick={golive} disabled={busy} data-testid="golive-btn" className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-red-600 text-white font-bold disabled:opacity-60 hover:brightness-110">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Radio size={16} />} Go Live
          </button>
        ) : (
          <button onClick={offline} disabled={busy} data-testid="offline-btn" className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-white/10 text-white font-bold disabled:opacity-60 hover:bg-white/15">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Square size={16} />} End Stream
          </button>
        )}
      </div>
      <p className="mt-2 text-[11px] text-white/30">Tip: register your channel below and 7 Legendary turns your stream ON/OFF automatically.</p>
      <Link to="/help#live-streaming" data-testid="golive-howto-link" className="mt-1 inline-flex items-center gap-1.5 text-xs font-semibold text-[hsl(var(--primary))] hover:underline"><HelpCircle size={13} /> How to go live &amp; connect channels</Link>

      <ConnectAccounts />

      <div className="mt-6 pt-5 border-t border-white/5">
        <h3 className="text-sm font-bold text-white mb-1 flex items-center gap-2"><Satellite size={15} className="text-[hsl(var(--primary))]" /> Auto-Detect (register once)</h3>
        <p className="text-xs text-white/40 mb-3">Register your channel and 7 Legendary will automatically turn your stream ON/OFF when you go live on the platform — no button needed.</p>
        <div className="grid sm:grid-cols-[auto_1fr_auto] gap-2 items-end">
          <div>
            <label className="text-xs text-white/50 mb-1 block">Platform</label>
            <select value={cf.platform} onChange={(e) => setCf({ ...cf, platform: e.target.value })} data-testid="autodetect-platform" className={inp}>
              <option value="youtube">YouTube</option><option value="twitch">Twitch</option><option value="kick">Kick</option>
            </select>
          </div>
          <div><label className="text-xs text-white/50 mb-1 block">Channel URL</label><input className={inp} value={cf.channel_url} onChange={(e) => setCf({ ...cf, channel_url: e.target.value })} placeholder="https://youtube.com/@yourchannel" data-testid="autodetect-url" /></div>
          <button onClick={addChannel} disabled={cbusy} data-testid="autodetect-save" className="px-4 py-2.5 rounded-lg bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-bold text-sm disabled:opacity-60">{cbusy ? <Loader2 size={15} className="animate-spin" /> : "Register"}</button>
        </div>
        <p className="mt-1.5 text-[11px] text-white/30">
          Detection status: {["youtube", "twitch", "kick"].map((p) => <span key={p} className="mr-2">{p} {detect[p] ? <span className="text-[hsl(var(--primary))]">ready</span> : <span className="text-white/40">key pending</span>}</span>)}
        </p>
        {chs?.length > 0 && (
          <div className="mt-3 space-y-2" data-testid="autodetect-list">
            {chs.map((c) => (
              <div key={c.platform} className="flex items-center gap-2 text-sm p-2.5 rounded-lg bg-white/[0.03]">
                <span className="text-xs font-bold uppercase text-white/70 w-16">{c.platform}</span>
                <span className="flex-1 min-w-0 truncate text-white/60">{c.ident}</span>
                {c.verified && <span data-testid={`autodetect-verified-${c.platform}`} className="inline-flex items-center gap-1 text-[11px] font-bold text-[hsl(var(--primary))]"><ShieldCheck size={12} /> verified</span>}
                {c.last_live && <span className="inline-flex items-center gap-1 text-[11px] text-[hsl(var(--primary))]"><CheckCircle2 size={12} /> live</span>}
                <button onClick={() => removeChannel(c.platform)} data-testid={`autodetect-remove-${c.platform}`} className="p-1.5 rounded text-white/40 hover:text-red-400"><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      {data.history?.length > 0 && (
        <div className="mt-6 pt-5 border-t border-white/5">
          <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2"><History size={15} /> Streaming History</h3>
          <div className="space-y-2" data-testid="stream-history">
            {data.history.map((h) => (
              <div key={h.id} className="flex items-center gap-3 text-sm p-2.5 rounded-lg bg-white/[0.03]">
                <span className="text-xs font-bold uppercase px-2 py-0.5 rounded" style={{ background: (PLATFORMS[h.platform]?.color || "#333") + "22", color: PLATFORMS[h.platform]?.color || "#fff" }}>{PLATFORMS[h.platform]?.label || h.platform}</span>
                <div className="flex-1 min-w-0"><div className="text-white truncate">{h.title}</div><div className="text-[11px] text-white/40">{(h.date || "").slice(0, 10)} · {h.game}</div></div>
                <span className="flex items-center gap-1 text-xs text-white/60"><Heart size={11} className="text-red-400" /> {h.likes}</span>
                <span className="flex items-center gap-1 text-xs text-white/60"><Eye size={11} /> {h.viewers}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
